<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue';
import { useAudioPlayer } from '@/composables/useAudioPlayer';
import { useActionFeedback } from '@/composables/useActionFeedback';
import ActionFeedbackOverlay from '@/components/feedback/ActionFeedbackOverlay.vue';
import { useDisplay } from 'vuetify';
import { sharePost, isMobileDevice } from '@/utils/shareUtils';
import { useAuth } from '@/composables/useAuth';

// Shared player control components
import TrackInfo from './controls/TrackInfo.vue';
import ProgressBar from './controls/ProgressBar.vue';
import PlaybackControls from './controls/PlaybackControls.vue';
import RatingSection from './controls/RatingSection.vue';
import VolumeControl from './controls/VolumeControl.vue';

interface Props {
  modelValue: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const audioPlayer = useAudioPlayer();
const { mobile } = useDisplay();
const { feedback, showFeedback } = useActionFeedback();
const { user: currentUser } = useAuth();

// Computed properties
const currentPost = computed(() => audioPlayer.currentTrack.value);
const currentTime = computed(() => audioPlayer.currentTime.value);
const queue = computed(() => audioPlayer.queue.value);
const queueIndex = computed(() => audioPlayer.queueIndex.value);
const repeatMode = computed(() => audioPlayer.repeatMode.value);
const isPlayingAd = computed(() => audioPlayer.isPlayingAd.value);
const isRadioStationMode = computed(() => audioPlayer.isRadioStationMode.value);

// Spotify-style: dim previous when at start (unless repeat is on)
const hasPrevious = computed(() => {
  if (repeatMode.value !== 'off') {
    return queue.value.length > 0;
  }
  return queueIndex.value > 0;
});

// Spotify-style: dim next when at end (unless repeat is on)
const hasNext = computed(() => {
  if (repeatMode.value !== 'off') return true;
  return queueIndex.value < queue.value.length - 1;
});

function skipToPrevious() {
  audioPlayer.playPrevious();
}

// Handle browser back button when player is open
function handlePopState(event: PopStateEvent) {
  // Only handle if player is open
  if (!props.modelValue) return;
  
  // Mark that we're handling this popstate to prevent App.vue handler
  (window as any).__playerHandlingPopstate = true;
  
  // Reset the flag after handlers have had a chance to check it
  setTimeout(() => {
    (window as any).__playerHandlingPopstate = false;
  }, 0);
  
  // If in the middle of the song (more than 2 seconds), seek to beginning
  if (currentTime.value > 2) {
    audioPlayer.seek(0);
  } else {
    // If within 2 seconds of beginning, go to previous song
    if (hasPrevious.value) {
      skipToPrevious();
    } else {
      // No previous song, close the player
      close();
      // Don't push state if closing - let it navigate normally
      return;
    }
  }
  
  // Push a new state to prevent navigation away (popstate events aren't cancelable)
  // This keeps the user on the current page after handling the back button
  window.history.pushState({ playerBackHandled: true }, '');
}

// Watch for player open/close to manage popstate listener
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      // Push a state when opening so we can intercept back button
      // This creates a history entry that we can detect when back is pressed
      window.history.pushState({ playerOpen: true }, '');
      window.addEventListener('popstate', handlePopState);
    } else {
      window.removeEventListener('popstate', handlePopState);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopState);
});


// Share handler
async function handleShare() {
  if (!currentPost.value) return;

  try {
    // Get referral code from current user
    const referralCode = currentUser.value?.referralCode as string | undefined;
    const result = await sharePost(currentPost.value, undefined, referralCode);

    if (result === 'success') {
      // Show overlay only when clipboard was used (desktop)
      // Mobile uses native share sheet which provides its own feedback
      if (!isMobileDevice()) {
        showFeedback('mdi-check', 'Link copied', 'success');
      }
    } else if (result === 'error') {
      // Show error only for actual failures, not user cancellation
      showFeedback('mdi-alert-circle-outline', 'Copy failed', 'error');
    }
    // 'cancelled' - user dismissed share dialog, no feedback needed
  } catch (error) {
    console.error('Failed to share:', error);
    showFeedback('mdi-alert-circle-outline', 'Share failed', 'error');
  }
}

// Set volume to 100% on mobile devices (users control via device buttons)
watch(
  mobile,
  (isMobile) => {
    if (isMobile) {
      audioPlayer.setVolume(100);
    }
  },
  { immediate: true }
);

// Close handler
function close() {
  emit('update:modelValue', false);
}

// Touch handling for swipe-down gesture
const touchStartY = ref(0);
const touchCurrentY = ref(0);
const isDragging = ref(false);
const dragOffset = ref(0);
const contentRef = ref<HTMLElement | null>(null);

function handleTouchStart(e: TouchEvent) {
  touchStartY.value = e.touches[0].clientY;
  isDragging.value = true;
}

function handleTouchMove(e: TouchEvent) {
  if (!isDragging.value) return;

  touchCurrentY.value = e.touches[0].clientY;
  const diff = touchCurrentY.value - touchStartY.value;

  // Check if content is scrolled to top
  const content = contentRef.value;
  const isAtTop = !content || content.scrollTop === 0;

  // If pulling down while at top, prevent default (stops pull-to-refresh)
  if (diff > 0 && isAtTop) {
    e.preventDefault();
    dragOffset.value = diff;
  }
}

function handleTouchEnd() {
  if (!isDragging.value) return;

  // If dragged down more than 150px, close the player
  if (dragOffset.value > 150) {
    close();
  }

  // Reset
  isDragging.value = false;
  dragOffset.value = 0;
  touchStartY.value = 0;
  touchCurrentY.value = 0;
}
</script>

<template>
  <!-- Action Feedback Overlay -->
  <ActionFeedbackOverlay
    :show="feedback.show"
    :icon="feedback.icon"
    :text="feedback.text"
    :type="feedback.type"
  />

  <v-dialog
    :model-value="modelValue"
    fullscreen
    transition="dialog-bottom-transition"
    class="full-screen-player"
    :scrim="false"
    persistent
    :z-index="10000"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div
      class="full-screen-player__container"
      :style="{
        transform: isDragging ? `translateY(${dragOffset}px)` : 'translateY(0)',
        transition: isDragging ? 'none' : 'transform 0.3s ease-out'
      }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- Background with album art blur -->
      <div
        v-if="currentPost?.images?.[0]"
        class="full-screen-player__background"
        :style="{
          backgroundImage: `url(${(currentPost.images[0] as any).url})`
        }"
      />

      <!-- Gradient overlay -->
      <div class="full-screen-player__overlay" />

      <!-- Header with close button -->
      <div class="full-screen-player__header">
        <v-btn
          icon
          variant="text"
          size="small"
          class="full-screen-player__close"
          @click="close"
        >
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </div>

      <!-- Main content -->
      <div ref="contentRef" class="full-screen-player__content">
        <!-- Track Info with Large Artwork -->
        <TrackInfo
          :track="currentPost"
          size="large"
          layout="vertical"
          class="full-screen-player__track-info"
        />

        <!-- Progress Bar -->
        <ProgressBar
          layout="stacked"
          color="primary"
          class="full-screen-player__progress"
        />

        <!-- Playback Controls -->
        <PlaybackControls
          size="large"
          :show-shuffle="!isRadioStationMode"
          :show-repeat="!isRadioStationMode"
          :show-previous="!isRadioStationMode"
          :disable-shuffle="isPlayingAd"
          :disable-previous="isPlayingAd"
          :disable-next="isPlayingAd || !hasNext"
          :disable-repeat="isPlayingAd"
          icon-color="primary"
          class="full-screen-player__controls"
        />

        <!-- Rating Section with Share Button -->
        <div class="full-screen-player__rating-section">
          <RatingSection :post-ulid="currentPost?.ulid" />

          <!-- Share Button -->
          <v-btn
            icon
            variant="text"
            size="large"
            color="primary"
            class="full-screen-player__share-btn"
            @click="handleShare"
          >
            <v-icon size="28">mdi-share-variant</v-icon>
          </v-btn>
        </div>

        <!-- Volume Control (Desktop only) -->
        <VolumeControl
          v-if="!mobile"
          popup-mode
          show-label
          class="full-screen-player__volume"
        />
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss">
// Ensure dialog appears above everything - must be unscoped for v-dialog
.full-screen-player {
  &.v-dialog {
    z-index: var(--z-fullscreen-player) !important;
  }

  .v-overlay__content {
    z-index: var(--z-fullscreen-player) !important;
  }

  &__container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(18, 18, 18, 0.95) 0%, rgba(18, 18, 18, 0.98) 100%);
    // Prevent pull-to-refresh and overscroll
    overscroll-behavior: contain;
    touch-action: pan-y;
  }

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    filter: blur(80px);
    opacity: 0.3;
    z-index: var(--z-base);
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.4) 0%,
      rgba(0, 0, 0, 0.8) 100%
    );
    z-index: var(--z-base-overlay);
  }

  &__header {
    position: relative;
    z-index: var(--z-sticky-content);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    // iOS safe area inset for notch/dynamic island
    padding-top: calc(16px + env(safe-area-inset-top, 0px));
    min-height: 56px;
  }

  &__close {
    color: white !important;
    z-index: 101;
  }

  &__content {
    position: relative;
    z-index: var(--z-base-content);
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    // iOS safe area inset for home indicator
    padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    gap: 32px;
    overflow-y: auto;
    // Prevent pull-to-refresh on content scroll
    overscroll-behavior-y: contain;
  }

  &__track-info {
    width: 100%;
    max-width: 500px;
  }

  &__progress {
    width: 100%;
    max-width: 500px;
  }

  &__controls {
    width: 100%;
    max-width: 500px;
  }

  &__rating-section {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    max-width: 500px;
    min-height: 60px;
  }

  &__share-btn {
    flex-shrink: 0;
  }

  &__volume {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// Mobile specific styles
@media (max-width: 768px) {
  .full-screen-player {
    &__content {
      padding: 16px;
      gap: 24px;
    }
  }
}
</style>
